// =============================================================================
// CalibrationDeck — Tinder-style verification UI
//
// Renders AFTER a clone has been trained. Lets the user:
//   1. Generate a fresh batch of AI-written sample sentences in this
//      clone's voice (2 per intent category by default).
//   2. Swipe through them — ✓ "yes, this is my voice" / ✗ "this missed".
//   3. See per-category confidence (approved/attempts as % ring).
//
// The component is intentionally self-contained: it owns its own batch +
// deck state, drives the Cloud Function via useCalibration, and writes
// feedback directly to RTDB at session end. No prop-drilled state from
// the editor.
//
// Why a separate component (vs. inline in CloneEditor):
//   - The Tinder mechanics (current index, animation, batched submit) are
//     enough complexity to warrant isolation.
//   - It can be opened from multiple entry points (post-train banner,
//     dedicated "Calibrate" button) without duplication.
//   - The whole calibration system is opt-in — the editor stays usable
//     without ever opening this section. Clean boundary.
// =============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CALIBRATION_CATEGORIES,
  CALIBRATION_TARGET_PER_CATEGORY,
  HARD_REJECT_CAP,
  categoryConfidence,
  categoryStatus,
  categoryCoverage,
  calibrationCoverage,
  calibrationReadiness,
  overallCalibrationConfidence,
  collectRejects,
  computeCategoryRoundCounts,
  SUBJECT_CALIBRATION_CATEGORIES,
  subjectCalibrationCoverage,
  subjectCalibrationReadiness,
  overallSubjectCalibrationConfidence,
  collectSubjectRejects,
} from "./cloneUtils";
import { useCalibration } from "./useCalibration";
import { renderSlotted } from "./renderSlotted";
import CalibrationDuel from "./CalibrationDuel";

// =============================================================================
// DECK VARIANTS — body (phrase-intent beats) vs subject (open-worthy tactics).
// The deck mechanics are identical; only the category list, the coverage /
// readiness helpers, the RTDB key, the preference dimensions and a handful of
// copy strings differ. Selecting a config keeps the component body-agnostic so
// the same Tinder UX powers both decks (see SubjectCalibrationDeck.js).
// =============================================================================
const BODY_DIMENSION_AXES = [
  { key: "directness", neg: "Indirect", pos: "Direct" },
  { key: "hedging", neg: "No hedging", pos: "Hedged" },
  { key: "formality", neg: "Casual", pos: "Formal" },
  { key: "sentenceLength", neg: "Short", pos: "Flowing" },
  { key: "warmth", neg: "Cool", pos: "Warm" },
  { key: "energy", neg: "Calm", pos: "High-energy" },
];

const SUBJECT_DIMENSION_AXES = [
  { key: "length", neg: "Short", pos: "Longer" },
  { key: "curiosity", neg: "Plain", pos: "Curiosity" },
  { key: "directness", neg: "Teasing", pos: "Blunt" },
  { key: "playfulness", neg: "Serious", pos: "Playful" },
  { key: "formality", neg: "Casual", pos: "Formal" },
];

export const DECK_CONFIGS = {
  body: {
    mode: "body",
    calibrationKey: "calibration",
    categories: CALIBRATION_CATEGORIES,
    // GROUPS — a full round is generated as several FOCUSED parallel calls,
    // one per group. Each group is a semantically coherent slice of the email
    // (opening / positioning / value / closing) so the model writes with one
    // consistent frame per call → sharper, more consistent samples than a
    // single 14-category dump. `other` is intentionally excluded from the
    // default round (rarely worth calibrating); it stays reachable via a
    // focused top-up.
    groups: [
      ["greeting", "trigger", "relevance"],
      ["sender_positioning", "audience_fit", "differentiation"],
      ["problem", "insight", "solution", "proof"],
      ["risk_reversal", "cta", "signature"],
    ],
    coverageFn: calibrationCoverage,
    readinessFn: calibrationReadiness,
    overallConfidenceFn: overallCalibrationConfidence,
    collectRejectsFn: collectRejects,
    dimensionAxes: BODY_DIMENSION_AXES,
    unitSingular: "beat",
    unitPlural: "beats",
    titleIcon: "fa-check-double",
    title: "Voice Calibration",
    intro:
      "The AI generates fresh sentences in your voice for each beat type. " +
      "You swipe \u2713 if it sounds like you, \u2717 if it missed. Approved " +
      "sentences become small extra style anchors on top of your real " +
      "samples. Rejected ones refine the deck \u2014 and from the misses you " +
      "can pin the few worst ones below, which the email writer will then " +
      "hard-avoid. Your real voice (from the samples) always stays the foundation.",
    ctaTitle: "Ready to calibrate",
    ctaSub:
      "The AI writes fresh sentences in your voice \u2014 swipe through them to " +
      "confirm what sounds like you.",
    generatingText: "Generating sentences in your voice\u2026",
    cardHint: (
      <>
        Judge the <strong>rhythm + structure</strong>, not the placeholders in
        «brackets» — those get filled with your real campaign content at
        send-time.
      </>
    ),
    approveText: "Sounds like me",
    rejectText: "Not me",
    doneBody: (
      <>
        Your feedback is now part of the clone. Approved sentences become small
        extra style anchors the email writer will lean toward; rejected ones
        refine the deck. Scroll up to <strong>Worst misses</strong> to pin the
        few you hated most — those become a hard avoid-list for the email
        writer. Your real samples stay the foundation of the voice —
        calibration only refines, it never overwrites.
      </>
    ),
  },
  subject: {
    mode: "subject",
    calibrationKey: "subjectCalibration",
    categories: SUBJECT_CALIBRATION_CATEGORIES,
    // Two focused groups across the 6 open-worthy tactics (curiosity-led vs.
    // direct/benefit-led), generated in parallel like the body round.
    groups: [
      ["curiosity_gap", "pattern_interrupt", "specific_observation"],
      ["question_hook", "benefit_tease", "personal_direct"],
    ],
    coverageFn: subjectCalibrationCoverage,
    readinessFn: subjectCalibrationReadiness,
    overallConfidenceFn: overallSubjectCalibrationConfidence,
    collectRejectsFn: collectSubjectRejects,
    dimensionAxes: SUBJECT_DIMENSION_AXES,
    unitSingular: "tactic",
    unitPlural: "tactics",
    titleIcon: "fa-heading",
    title: "Subject Calibration",
    intro:
      "The AI generates fresh subject lines in your voice for each open-worthy " +
      "tactic. You swipe \u2713 if it's a subject you'd send, \u2717 if it " +
      "missed. Approved subjects teach your CopyTwin the subject styles you " +
      "love; rejected ones refine the deck \u2014 and from the misses you can " +
      "pin the worst ones below, which the subject writer will then hard-avoid. " +
      "Your real subject lines always stay the foundation.",
    ctaTitle: "Ready to calibrate subjects",
    ctaSub:
      "The AI writes fresh subject lines in your voice \u2014 swipe through them " +
      "to confirm which subjects sound like you.",
    generatingText: "Generating subject lines in your voice\u2026",
    cardHint: (
      <>
        Judge whether this <strong>subject line sounds like you</strong> — the
        «brackets» get filled with the real recipient's details at send-time.
      </>
    ),
    approveText: "I'd send this",
    rejectText: "Not me",
    doneBody: (
      <>
        Your feedback is now part of the clone. Approved subject lines teach the
        subject writer the styles you love; rejected ones refine the deck.
        Scroll up to <strong>Worst misses</strong> to pin the few you hated most
        — those become a hard avoid-list for the subject writer. Your real
        subject lines stay the foundation — calibration only refines, it never
        overwrites.
      </>
    ),
  },
};

// Map iso → language label so the cloud function generates in the right
// tongue. Mirror of the backend lookup; keeping it client-side avoids a
// round-trip just to learn what label to send.
const ISO_TO_LANGUAGE = {
  en: "English", de: "German", fr: "French", es: "Spanish",
  it: "Italian", nl: "Dutch", pt: "Portuguese",
};

const resolveLanguage = (clone) => {
  const iso = clone?.language && clone.language !== "auto" ? clone.language : null;
  return (iso && ISO_TO_LANGUAGE[iso]) || "auto";
};

// =============================================================================
// CONFIDENCE RING — small inline SVG ring rendering 0-100%. Color tones
// mirror the voice DNA preview's confidence chips for visual continuity.
// =============================================================================
// `tone` can be forced (used by the per-beat coverage rings so the colour
// reflects MATURITY, not approval rate); otherwise it's derived from `value`.
// `centerText` overrides the default "NN%" label (e.g. a check icon when a
// beat is fully calibrated, or a raw swipe count while in progress).
const ConfidenceRing = ({
  value,
  size = 36,
  stroke = 4,
  label = null,
  tone: toneOverride = null,
  centerText,
}) => {
  const empty = value === null || value === undefined;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = empty ? circumference : circumference * (1 - value / 100);
  const tone =
    toneOverride ||
    (empty ? "empty" : value >= 75 ? "high" : value >= 50 ? "mid" : "low");
  const center =
    centerText !== undefined ? centerText : empty ? "–" : `${value}%`;
  return (
    <div className={`cca-calib-ring cca-calib-ring--${tone}`} style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          className="cca-calib-ring-track"
          strokeWidth={stroke}
          fill="none"
        />
        {!empty && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth={stroke}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        )}
      </svg>
      <div className="cca-calib-ring-label">{center}</div>
      {label && <div className="cca-calib-ring-sub">{label}</div>}
    </div>
  );
};

// =============================================================================
// CALIBRATION SUMMARY — per-category strip with confidence rings + counts.
// Visible above the deck so the user can see at a glance which beats are
// still under-calibrated. Clicking a chip narrows the next batch to JUST
// that category (instant "fix this beat" workflow).
// =============================================================================
// Tone → SVG ring colour bucket reused from ConfidenceRing's vocabulary.
const STATUS_TONE = { untouched: "empty", started: "mid", calibrated: "high" };

const CalibrationSummary = ({
  cfg,
  calibration,
  focusCategory,
  onFocus,
  onClear,
  onCalibrateGaps,
  generating,
  collapsible,
  expanded,
  onToggle,
}) => {
  const coverage = cfg.coverageFn(calibration);
  const readiness = cfg.readinessFn(calibration);
  const agreement = cfg.overallConfidenceFn(calibration); // null until any swipe
  const started = (calibration?.totalAttempts || 0) > 0;

  // When collapsible and collapsed, only the overall bar stays; the per-beat
  // grid + gap nudge fold away so calibrated decks don't sprawl down the page.
  const showDetails = !collapsible || expanded;

  // Names of the units that still need work, for the "what's left" nudge.
  const gapNames = coverage.undercalibrated
    .map((id) => cfg.categories.find((c) => c.id === id)?.short)
    .filter(Boolean);

  return (
    <div className="cca-calib-summary">
      <div
        className={`cca-calib-summary-overall ${
          collapsible ? "cca-calib-summary-overall--toggle" : ""
        }`}
        role={collapsible ? "button" : undefined}
        tabIndex={collapsible ? 0 : undefined}
        aria-expanded={collapsible ? expanded : undefined}
        onClick={collapsible ? onToggle : undefined}
        onKeyDown={
          collapsible
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onToggle();
                }
              }
            : undefined
        }
      >
        <ConfidenceRing
          value={coverage.percent}
          size={60}
          stroke={5}
          tone={readiness.tone}
          centerText={`${coverage.percent}%`}
        />
        <div className="cca-calib-summary-overall-text">
          <div className="cca-calib-summary-overall-head">
            <span className="cca-calib-summary-overall-title">{cfg.title}</span>
            <span className={`cca-calib-stage cca-calib-stage--${readiness.tone}`}>
              {readiness.id === "complete" && <i className="fas fa-circle-check" />}
              {readiness.label}
            </span>
          </div>
          <div className="cca-calib-summary-overall-sub">
            <strong>{coverage.calibrated} of {coverage.total} {cfg.unitPlural}</strong> calibrated
            {started && agreement !== null && (
              <> · {agreement}% sounded like you</>
            )}
            {" — "}{readiness.hint}
          </div>
        </div>
        {collapsible && (
          <span className="cca-calib-summary-toggle-hint">
            {expanded ? "Hide details" : "Show details"}
            <i
              className={`fas fa-chevron-down cca-calib-summary-chevron ${
                expanded ? "cca-calib-summary-chevron--open" : ""
              }`}
            />
          </span>
        )}
      </div>

      {showDetails && coverage.undercalibrated.length > 0 && started && (
        <div className="cca-calib-gaps">
          <span className="cca-calib-gaps-text">
            <i className="fas fa-list-check" />
            <span>
              <strong>{coverage.undercalibrated.length} {coverage.undercalibrated.length === 1 ? cfg.unitSingular : cfg.unitPlural} still need a few cards:</strong>{" "}
              {gapNames.slice(0, 6).join(", ")}
              {gapNames.length > 6 ? "…" : ""}
            </span>
          </span>
          <button
            type="button"
            className="cca-calib-gaps-btn"
            onClick={onCalibrateGaps}
            disabled={generating}
          >
            <i className="fas fa-wand-magic-sparkles" /> Calibrate what&apos;s left
          </button>
        </div>
      )}

      {showDetails && (
      <div className="cca-calib-summary-grid">
        {cfg.categories.map((cat) => {
          const c = calibration?.categories?.[cat.id];
          const status = categoryStatus(c);
          const coveragePct = Math.round(categoryCoverage(c) * 100);
          const conf = categoryConfidence(c);
          const isFocused = focusCategory === cat.id;
          const attempts = c?.attempts || 0;

          const center =
            status === "calibrated" ? (
              <i className="fas fa-check" />
            ) : status === "started" ? (
              attempts
            ) : (
              "–"
            );

          const countsLine =
            status === "untouched"
              ? "Not calibrated"
              : status === "started"
                ? `${attempts}/${CALIBRATION_TARGET_PER_CATEGORY} swipes`
                : `${conf}% sounded like you`;

          return (
            <button
              key={cat.id}
              type="button"
              className={`cca-calib-cat-chip cca-calib-cat-chip--${status} ${
                isFocused ? "cca-calib-cat-chip--focused" : ""
              }`}
              onClick={() => (isFocused ? onClear() : onFocus(cat.id))}
              title={
                isFocused
                  ? "Click again to widen back to all categories"
                  : status === "calibrated"
                    ? `${cat.label} is calibrated — click to top it up`
                    : `Focus the next batch on ${cat.short} only`
              }
            >
              <ConfidenceRing
                value={status === "untouched" ? null : coveragePct}
                size={42}
                stroke={4}
                tone={STATUS_TONE[status]}
                centerText={center}
              />
              <div className="cca-calib-cat-chip-meta">
                <div className="cca-calib-cat-chip-label">
                  <i className={`fas ${cat.icon}`} /> {cat.short}
                </div>
                <div className="cca-calib-cat-chip-counts">{countsLine}</div>
              </div>
            </button>
          );
        })}
      </div>
      )}
    </div>
  );
};

// =============================================================================
// PREFERENCE PROFILE — "what your CopyTwin actually learned from your swipes".
// This is the payoff that was previously computed (distillCalibrationPreferences)
// but NEVER shown. Surfacing it answers "how much does this help?".
// =============================================================================
const PreferenceProfilePanel = ({ cfg, profile, distilling }) => {
  // Learning in progress, nothing distilled yet.
  if (!profile) {
    if (distilling) {
      return (
        <div className="cca-calib-learn cca-calib-learn--pending">
          <i className="fas fa-spinner fa-spin" />
          <span>Learning your style from this session…</span>
        </div>
      );
    }
    return null;
  }

  const dims = cfg.dimensionAxes.filter(
    (d) => typeof profile.dimensions?.[d.key] === "number",
  );

  return (
    <div className="cca-calib-learn">
      <div className="cca-calib-learn-head">
        <div className="cca-calib-learn-title">
          <i className="fas fa-brain" /> What your CopyTwin learned
        </div>
        {distilling ? (
          <span className="cca-calib-learn-status">
            <i className="fas fa-spinner fa-spin" /> updating…
          </span>
        ) : (
          profile.derivedFrom && (
            <span className="cca-calib-learn-status">
              from {profile.derivedFrom.approved || 0}✓ / {profile.derivedFrom.rejected || 0}✗
            </span>
          )
        )}
      </div>

      {profile.summary && (
        <p className="cca-calib-learn-summary">{profile.summary}</p>
      )}

      {(profile.likes?.length > 0 || profile.dislikes?.length > 0) && (
        <div className="cca-calib-learn-lists">
          {profile.likes?.length > 0 && (
            <div className="cca-calib-learn-col">
              <div className="cca-calib-learn-col-title cca-calib-learn-col-title--like">
                <i className="fas fa-thumbs-up" /> Leans toward
              </div>
              <ul>
                {profile.likes.map((t, i) => (
                  <li key={`l${i}`}>{t}</li>
                ))}
              </ul>
            </div>
          )}
          {profile.dislikes?.length > 0 && (
            <div className="cca-calib-learn-col">
              <div className="cca-calib-learn-col-title cca-calib-learn-col-title--dislike">
                <i className="fas fa-thumbs-down" /> Steers away from
              </div>
              <ul>
                {profile.dislikes.map((t, i) => (
                  <li key={`d${i}`}>{t}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {dims.length > 0 && (
        <div className="cca-calib-dims">
          {dims.map((d) => {
            const v = profile.dimensions[d.key]; // -1..1
            const pct = ((v + 1) / 2) * 100;
            return (
              <div className="cca-calib-dim" key={d.key}>
                <span className="cca-calib-dim-pole">{d.neg}</span>
                <span className="cca-calib-dim-track">
                  <span className="cca-calib-dim-dot" style={{ left: `${pct}%` }} />
                </span>
                <span className="cca-calib-dim-pole cca-calib-dim-pole--pos">{d.pos}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// WORST MISSES — the user pins the EXTREME rejects so the email writer
// hard-avoids that exact style. This is the one place the calibration system
// feeds rejects (not just approvals) into generation — but ONLY the curated
// extremes (capped), never the full reject pile, so it stays sharp and lean.
// =============================================================================
const norm = (t) => String(t || "").replace(/\s+/g, " ").trim().toLowerCase();

const WorstMissesPanel = ({ cfg, calibration, onToggle }) => {
  const pinned = useMemo(
    () => (Array.isArray(calibration?.hardRejects) ? calibration.hardRejects : []),
    [calibration],
  );
  const candidates = useMemo(() => cfg.collectRejectsFn(calibration), [cfg, calibration]);

  // Merge pinned-first, then the recent reject candidates. Pinned entries that
  // are no longer in the recent window must still render so they can be unpinned.
  const items = useMemo(() => {
    const seen = new Set();
    const merged = [];
    for (const p of pinned) {
      const key = norm(p?.text);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      merged.push({ text: p.text, categoryId: p.categoryId, pinned: true });
    }
    for (const c of candidates) {
      const key = norm(c.text);
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push({ ...c, pinned: false });
    }
    return merged;
  }, [pinned, candidates]);

  // Nothing rejected yet → nothing to curate.
  if (items.length === 0) return null;

  const atCap = pinned.length >= HARD_REJECT_CAP;

  return (
    <div className="cca-calib-worst">
      <div className="cca-calib-worst-head">
        <div className="cca-calib-worst-title">
          <i className="fas fa-ban" /> Worst misses
          <span className="cca-calib-worst-count">
            {pinned.length}/{HARD_REJECT_CAP}
          </span>
        </div>
        <p className="cca-calib-worst-sub">
          Pin the {cfg.mode === "subject" ? "subject lines" : "sentences"} you
          find the <strong>worst</strong>. The {cfg.mode === "subject" ? "subject" : "email"}{" "}
          writer will hard-avoid this exact style. Keep it to a few real
          offenders — only the extremes stay sharp; a long list would just
          confuse the AI and water down your real voice.
        </p>
      </div>
      <div className="cca-calib-worst-list">
        {items.map((m, i) => {
          const cat = cfg.categories.find((c) => c.id === m.categoryId);
          const disabled = !m.pinned && atCap;
          return (
            <button
              key={`${i}-${m.pinned ? "p" : "c"}`}
              type="button"
              className={`cca-calib-worst-item ${
                m.pinned ? "cca-calib-worst-item--pinned" : ""
              }`}
              onClick={() => onToggle(m)}
              disabled={disabled}
              title={
                m.pinned
                  ? "Pinned — the writer hard-avoids this. Click to unpin."
                  : atCap
                    ? `Limit reached (${HARD_REJECT_CAP}). Unpin one first.`
                    : "Pin as a worst miss — the writer will hard-avoid this style."
              }
            >
              <span className="cca-calib-worst-mark">
                <i className={`fas ${m.pinned ? "fa-ban" : "fa-plus"}`} />
              </span>
              <span className="cca-calib-worst-text">
                "{renderSlotted(m.text)}"
              </span>
              {cat && <span className="cca-calib-worst-cat">{cat.short}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// =============================================================================
// GENERATING PROGRESS — a full round is now generated as several FOCUSED
// parallel calls, so we can show HONEST progress: the bar's solid floor is the
// fraction of group calls that have actually completed (`progress.done /
// progress.total`), and within the in-flight group we add a small time-based
// creep so the bar never sits frozen. If no `progress` is passed (a single
// focused top-up call), we fall back to the pure time estimate.
// =============================================================================
const GENERATING_STEPS = {
  body: [
    "Loading your voice DNA\u2026",
    "Studying your phrase patterns\u2026",
    "Writing fresh sentences in your voice\u2026",
    "Checking the rhythm sounds like you\u2026",
    "Polishing the final phrasing\u2026",
  ],
  subject: [
    "Loading your voice DNA\u2026",
    "Studying your subject-line patterns\u2026",
    "Writing fresh subject lines in your voice\u2026",
    "Checking they sound like you\u2026",
    "Polishing the final wording\u2026",
  ],
};

const GeneratingProgress = ({ mode = "body", progress = null, expectedMs = 30000 }) => {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(Date.now());

  // Restart the within-block creep timer whenever a group completes, so the
  // creep represents "time into the CURRENT block", not since mount.
  const done = progress?.done ?? 0;
  useEffect(() => {
    startRef.current = Date.now();
    setElapsed(0);
    const id = setInterval(() => setElapsed(Date.now() - startRef.current), 200);
    return () => clearInterval(id);
  }, [done]);

  const steps = GENERATING_STEPS[mode] || GENERATING_STEPS.body;
  const seconds = Math.floor(elapsed / 1000);

  let pct;
  let foot;
  if (progress && progress.total > 0) {
    // REAL progress: solid floor from completed groups + a capped creep across
    // the in-flight group's share so the bar keeps breathing between completions.
    const blockShare = 1 / progress.total;
    const tau = (expectedMs / progress.total) / 2.2;
    const creep = (1 - Math.exp(-elapsed / tau)) * 0.85; // ≤85% into the block
    const raw = (progress.done + creep) * blockShare * 100;
    // Never reach 100% until the component unmounts (all data merged).
    pct = Math.min(97, Math.round(raw));
    foot = `${progress.done} of ${progress.total} parts ready \u00b7 writing in focused passes`;
  } else {
    const tau = expectedMs / 2.5;
    pct = Math.min(95, Math.round(95 * (1 - Math.exp(-elapsed / tau))));
    const overLong = elapsed > expectedMs * 2.2;
    foot = overLong
      ? "Still working \u2014 the AI is taking a little longer than usual, hang tight\u2026"
      : `${seconds}s elapsed \u00b7 usually about 30 seconds`;
  }

  const stepIdx = Math.min(steps.length - 1, Math.floor(elapsed / 4000));

  return (
    <div className="cca-calib-genprog" role="status" aria-live="polite">
      <div className="cca-calib-genprog-head">
        <span className="cca-calib-genprog-msg">
          <i className="fas fa-wand-magic-sparkles" /> {steps[stepIdx]}
        </span>
        <span className="cca-calib-genprog-pct">{pct}%</span>
      </div>
      <div className="cca-calib-genprog-track">
        <span className="cca-calib-genprog-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="cca-calib-genprog-foot">{foot}</div>
    </div>
  );
};

// =============================================================================
// ROUND PLANNING — turn the config's static groups into the actual set of
// focused calls for THIS round, adaptively.
//
//   • First round (nothing calibrated yet): cover every group fully.
//   • Re-round (already has results): restrict each group to the categories
//     that still need work (under-calibrated), and drop now-empty groups —
//     so swipes are spent where the signal is missing, not re-confirming
//     settled beats. If everything is already calibrated, do a light top-up
//     across all groups (one fewer card per category).
// =============================================================================
const planRoundGroups = (cfg, calibration, basePerCategory) => {
  const allGroups = Array.isArray(cfg.groups) && cfg.groups.length
    ? cfg.groups
    : [cfg.categories.map((c) => c.id)];

  const started = (calibration?.totalAttempts || 0) > 0;
  if (!started) {
    const allIds = allGroups.flat();
    return {
      groups: allGroups,
      perCategory: basePerCategory,
      perCategoryById: computeCategoryRoundCounts(calibration, allIds, basePerCategory),
    };
  }

  const under = new Set(cfg.coverageFn(calibration).undercalibrated || []);
  const focused = allGroups
    .map((g) => g.filter((id) => under.has(id)))
    .filter((g) => g.length > 0);

  if (focused.length === 0) {
    // Fully calibrated but the user asked for another round → light refinement.
    const allIds = allGroups.flat();
    return {
      groups: allGroups,
      perCategory: Math.max(1, basePerCategory - 1),
      perCategoryById: computeCategoryRoundCounts(calibration, allIds, basePerCategory),
    };
  }

  const focusedIds = focused.flat();
  return {
    groups: focused,
    perCategory: basePerCategory,
    perCategoryById: computeCategoryRoundCounts(calibration, focusedIds, basePerCategory),
  };
};

// Interleave samples so the deck alternates categories instead of showing all
// N cards of one category back-to-back. Round-robins by category in arrival
// order — keeps swiping varied and stops the "3 greetings in a row" monotony.
const interleaveByCategory = (samples) => {
  const buckets = new Map();
  for (const s of samples) {
    if (!buckets.has(s.category)) buckets.set(s.category, []);
    buckets.get(s.category).push(s);
  }
  const lists = [...buckets.values()];
  const out = [];
  let added = true;
  while (added) {
    added = false;
    for (const list of lists) {
      const next = list.shift();
      if (next) {
        out.push(next);
        added = true;
      }
    }
  }
  return out;
};

// Default samples per category for a full round (user-chosen). Focused
// single-beat top-ups override this with a higher count for depth.
const BASE_PER_CATEGORY = 3;

// =============================================================================
// DECK CARD — the swipable sentence card. Animation is pure CSS class
// toggles (avoiding a dependency on react-spring / framer just for this).
// =============================================================================
const DeckCard = ({ sample, categoryLabel, categoryIcon, exitDir, hint }) => (
  <div className={`cca-calib-card ${exitDir ? `cca-calib-card--exit-${exitDir}` : ""}`}>
    <div className="cca-calib-card-cat">
      <i className={`fas ${categoryIcon}`} /> {categoryLabel}
    </div>
    <div className="cca-calib-card-text">
      "{renderSlotted(sample.text)}"
    </div>
    <div className="cca-calib-card-hint">{hint}</div>
  </div>
);

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const CalibrationDeck = ({ userId, cloneId, clone, variant = "body" }) => {
  const cfg = DECK_CONFIGS[variant] || DECK_CONFIGS.body;
  const {
    fetchSamples,
    fetchSamplesGrouped,
    submitFeedback,
    distillPreferences,
    toggleWorstReject,
    generating,
    genProgress,
    distilling,
    error,
    errorKind,
    clearError,
  } = useCalibration(userId, { mode: cfg.mode });
  const [deck, setDeck] = useState([]);          // [{ category, text }]
  const [index, setIndex] = useState(0);
  const [pendingEvents, setPendingEvents] = useState([]); // events not yet flushed
  const [focusCategory, setFocusCategory] = useState(null);
  const [exitDir, setExitDir] = useState(null);  // 'left' | 'right' | null
  const [sessionDone, setSessionDone] = useState(false);
  const [resultsExpanded, setResultsExpanded] = useState(false);
  // A/B duel refinement (body deck only). Replaces the swipe area while active.
  const [showDuel, setShowDuel] = useState(false);

  const language = useMemo(() => resolveLanguage(clone), [clone]);
  const calibration = clone?.[cfg.calibrationKey];
  const hasDna = !!clone?.voiceDNA;

  // Has the clone been calibrated before? Drives the empty-state copy: a
  // returning user already sees "Fully calibrated" up top, so the CTA must
  // explain that a new round REFINES (adds to) the existing calibration
  // rather than implying a fresh first-time setup. The results (per-beat grid
  // + learned-profile panel) get long, so they're always collapsed behind a
  // toggle by default — the user expands on demand.
  const hasResults = (calibration?.totalAttempts || 0) > 0;

  // Auto-flush pending swipes whenever they accumulate to 4+ OR the deck
  // ends. Bounded batches prevent the "user swiped 20 cards then closed
  // tab → lost feedback" failure mode while still keeping write count low.
  useEffect(() => {
    if (pendingEvents.length === 0) return;
    const shouldFlush = pendingEvents.length >= 4 || sessionDone;
    if (!shouldFlush) return;
    const events = pendingEvents;
    setPendingEvents([]);
    submitFeedback({
      cloneId,
      currentCalibration: calibration,
      events,
    });
  }, [pendingEvents, sessionDone, submitFeedback, cloneId, calibration]);

  // Auto-distill the STYLE PREFERENCE PROFILE once per finished session.
  // We can't piggy-back on the final flush: a session whose card count is
  // a multiple of 4 flushes its last batch via the ">=4" rule, leaving
  // pendingEvents empty by the time sessionDone flips — so we trigger on
  // sessionDone AND an empty pending queue (all swipes written). The hook's
  // distillPreferences internally awaits the last write, so even if that
  // write is still in flight here, the backend reads the fresh swipes.
  // distillStartedRef guards against re-firing on re-renders; it resets when
  // a new session starts (sessionDone flips back to false in startBatch).
  const distillStartedRef = useRef(false);
  useEffect(() => {
    if (!sessionDone) {
      distillStartedRef.current = false;
      return;
    }
    if (pendingEvents.length > 0) return;   // wait until the final batch is dispatched
    if (distillStartedRef.current) return;
    distillStartedRef.current = true;
    distillPreferences({ cloneId });
  }, [sessionDone, pendingEvents.length, distillPreferences, cloneId]);

  // Build the deck for a result, interleaving categories so swiping stays
  // varied. Shared by every entry point below.
  const applyResult = useCallback((result) => {
    if (result?.samples?.length) {
      setDeck(interleaveByCategory(result.samples));
    } else {
      setDeck([]);
    }
  }, []);

  // `override` lets callers request a specific set of beats (e.g. the
  // "calibrate what's left" gap-fill) as ONE focused call. With no override
  // and no focused beat, we run a full ADAPTIVE round: several focused group
  // calls in parallel (real progress), scoped to what still needs work.
  // Guarded so an onClick event (truthy but with no categoryIds) falls
  // through to the normal behaviour.
  const startBatch = useCallback(
    async (override) => {
      setSessionDone(false);
      setIndex(0);
      setExitDir(null);

      const overrideIds = Array.isArray(override?.categoryIds)
        ? override.categoryIds
        : null;

      if (overrideIds) {
        // Explicit set (gap-fill) → a single focused call.
        const counts = computeCategoryRoundCounts(
          calibration,
          overrideIds,
          override.perCategory || BASE_PER_CATEGORY,
        );
        const result = await fetchSamples({
          cloneId,
          categoryIds: overrideIds,
          perCategory: override.perCategory || BASE_PER_CATEGORY,
          perCategoryById: counts,
          language,
        });
        applyResult(result);
        return;
      }

      if (focusCategory) {
        // Single-beat top-up — deeper count for categories that need work.
        const counts = computeCategoryRoundCounts(calibration, [focusCategory], 4);
        const result = await fetchSamples({
          cloneId,
          categoryIds: [focusCategory],
          perCategory: counts[focusCategory] || 4,
          perCategoryById: counts,
          language,
        });
        applyResult(result);
        return;
      }

      // Full adaptive round → parallel focused group calls.
      const { groups, perCategory, perCategoryById } = planRoundGroups(cfg, calibration, BASE_PER_CATEGORY);
      const result = await fetchSamplesGrouped({
        cloneId,
        groups,
        perCategory,
        perCategoryById,
        language,
      });
      applyResult(result);
    },
    [fetchSamples, fetchSamplesGrouped, applyResult, cfg, calibration, cloneId, focusCategory, language],
  );

  // One-click gap-fill: generate cards for the beats that still need work,
  // grouped by core area and run in parallel (real progress) just like a full
  // round. Untouched beats are prioritised by the coverage ordering.
  const calibrateGaps = useCallback(async () => {
    setFocusCategory(null);
    setSessionDone(false);
    setIndex(0);
    setExitDir(null);
    const gaps = new Set(cfg.coverageFn(calibration).undercalibrated.slice(0, 8));
    if (gaps.size === 0) return;
    const baseGroups = Array.isArray(cfg.groups) && cfg.groups.length
      ? cfg.groups
      : [cfg.categories.map((c) => c.id)];
    const groups = baseGroups
      .map((g) => g.filter((id) => gaps.has(id)))
      .filter((g) => g.length > 0);
    if (!groups.length) return;
    const gapIds = [...gaps];
    const result = await fetchSamplesGrouped({
      cloneId,
      groups,
      perCategory: BASE_PER_CATEGORY,
      perCategoryById: computeCategoryRoundCounts(calibration, gapIds, BASE_PER_CATEGORY),
      language,
    });
    applyResult(result);
  }, [cfg, calibration, fetchSamplesGrouped, applyResult, cloneId, language]);

  // Pin / unpin a reject as a "worst miss" → feeds the email writer's hard
  // avoid-list. Persisted via the hook (writes only the hardRejects subpath).
  const handleToggleWorst = useCallback(
    (item) => {
      toggleWorstReject({
        cloneId,
        currentCalibration: calibration,
        text: item.text,
        categoryId: item.categoryId,
      });
    },
    [toggleWorstReject, cloneId, calibration],
  );

  const swipe = useCallback(
    (approved) => {
      const card = deck[index];
      if (!card) return;
      const dir = approved ? "right" : "left";
      setExitDir(dir);
      setPendingEvents((prev) => [
        ...prev,
        { categoryId: card.category, text: card.text, approved },
      ]);
      // Wait for the CSS exit transition (200ms) before stepping to next
      // card so the user sees the directional flick — purely cosmetic.
      setTimeout(() => {
        setExitDir(null);
        setIndex((i) => {
          const next = i + 1;
          if (next >= deck.length) {
            setSessionDone(true);
          }
          return next;
        });
      }, 200);
    },
    [deck, index],
  );

  // Keyboard accessibility — Arrow Left = reject, Arrow Right = approve.
  // Common pattern for swipe decks; lets keyboard users power-calibrate.
  useEffect(() => {
    const onKey = (e) => {
      if (deck.length === 0 || index >= deck.length || exitDir) return;
      if (e.key === "ArrowRight" || e.key === "Enter") swipe(true);
      if (e.key === "ArrowLeft" || e.key === "Backspace") swipe(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [swipe, deck.length, index, exitDir]);

  if (!hasDna) {
    return (
      <div className="cca-calib-empty">
        <i className="fas fa-circle-dashed" />
        <div>
          <h4>Calibration unlocks after training</h4>
          <p>
            Train your CopyTwin first — then you can verify that the AI actually
            understood your voice with a quick Tinder-style swipe deck.
          </p>
        </div>
      </div>
    );
  }

  const current = deck[index];
  const progress = deck.length ? Math.min(index, deck.length) : 0;

  // Context-aware empty-state copy. Three modes:
  //   focused      — user clicked a single beat chip to top it up
  //   recalibrate  — clone is already calibrated; a new round REFINES it
  //   firstTime    — never calibrated yet
  const focusedCat = focusCategory
    ? cfg.categories.find((c) => c.id === focusCategory)
    : null;
  const ctaMode = focusCategory ? "focused" : hasResults ? "recalibrate" : "first";
  const ctaCopy = {
    focused: {
      heading: `Top up ${focusedCat?.short || "this beat"}`,
      sub: `Generate a few more ${focusedCat?.short || "cards"} cards to push this single beat to full confidence. The rest of your calibration stays untouched.`,
      label: `Generate 3 ${focusedCat?.short || ""} cards`,
      icon: "fa-arrow-right-long",
    },
    recalibrate: {
      heading: "You're calibrated",
      sub: "Run another round anytime to refine — new swipes build on what's there, nothing gets reset.",
      label: "Run another round",
      icon: "fa-arrows-rotate",
    },
    first: {
      heading: cfg.ctaTitle,
      sub: cfg.ctaSub,
      label: "Start a full round",
      icon: "fa-arrow-right-long",
    },
  }[ctaMode];

  // Shared between the hero and the compact recalibrate strip.
  const errorBlock = error && (
    <div
      className={`cca-calib-error cca-calib-error-${errorKind || "unknown"}`}
      role="alert"
    >
      <i
        className={`fas ${
          errorKind === "transport"
            ? "fa-cloud-slash"
            : errorKind === "auth"
              ? "fa-user-lock"
              : errorKind === "precondition"
                ? "fa-circle-info"
                : "fa-triangle-exclamation"
        }`}
      />
      <div className="cca-calib-error-body">
        <div className="cca-calib-error-msg">{error}</div>
        <div className="cca-calib-error-actions">
          {errorKind !== "auth" && errorKind !== "precondition" && (
            <button
              type="button"
              className="cca-calib-error-btn cca-calib-error-btn-primary"
              onClick={() => {
                clearError();
                startBatch();
              }}
            >
              <i className="fas fa-rotate" /> Try again
            </button>
          )}
          <button type="button" className="cca-calib-error-btn" onClick={clearError}>
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="cca-calib">
      <div className="cca-calib-header">
        <div>
          <h3 className="cca-calib-title">
            <i className={`fas ${cfg.titleIcon}`} /> {cfg.title}
          </h3>
          <p className="cca-calib-sub">{cfg.intro}</p>
        </div>
      </div>

      <CalibrationSummary
        cfg={cfg}
        calibration={calibration}
        focusCategory={focusCategory}
        onFocus={setFocusCategory}
        onClear={() => setFocusCategory(null)}
        onCalibrateGaps={calibrateGaps}
        generating={generating}
        collapsible
        expanded={resultsExpanded}
        onToggle={() => setResultsExpanded((v) => !v)}
      />

      {resultsExpanded && (
        <>
          <PreferenceProfilePanel
            cfg={cfg}
            profile={calibration?.preferenceProfile}
            distilling={distilling}
          />

          <WorstMissesPanel
            cfg={cfg}
            calibration={calibration}
            onToggle={handleToggleWorst}
          />
        </>
      )}

      {showDuel && cfg.mode === "body" && (
        <CalibrationDuel
          userId={userId}
          cloneId={cloneId}
          clone={clone}
          language={language}
          onDone={() => setShowDuel(false)}
        />
      )}

      {!showDuel && (
      <div
        className={`cca-calib-deck-area ${
          deck.length === 0 && ctaMode === "recalibrate"
            ? "cca-calib-deck-area--compact"
            : ""
        }`}
      >
        {/* Empty state — no deck loaded yet */}
        {deck.length === 0 && ctaMode === "recalibrate" && (
          /* Compact, minimized strip — calibration is done; this is just the
             optional "refine again" affordance, so it stays small + calm. */
          <div className="cca-calib-recal">
            <div className="cca-calib-recal-main">
              <span className="cca-calib-recal-icon">
                <i className="fas fa-circle-check" />
              </span>
              <div className="cca-calib-recal-text">
                <div className="cca-calib-recal-title">{ctaCopy.heading}</div>
                <div className="cca-calib-recal-sub">{ctaCopy.sub}</div>
              </div>
              <button
                className="cca-calib-recal-btn"
                onClick={() => startBatch()}
                disabled={generating}
                type="button"
              >
                {generating ? (
                  <><i className="fas fa-spinner fa-spin" /> {cfg.generatingText}</>
                ) : (
                  <><i className={`fas ${ctaCopy.icon}`} /> {ctaCopy.label}</>
                )}
              </button>
              {cfg.mode === "body" && (
                <button
                  className="cca-calib-recal-btn cca-calib-recal-btn--secondary"
                  onClick={() => setShowDuel(true)}
                  disabled={generating}
                  type="button"
                  title="Pit each approved sentence against a sharper alternative and keep the better one"
                >
                  <i className="fas fa-code-compare" /> Refine A/B
                </button>
              )}
            </div>
            {generating && <GeneratingProgress mode={cfg.mode} progress={genProgress} />}
            {errorBlock}
          </div>
        )}

        {deck.length === 0 && ctaMode !== "recalibrate" && (
          <div className="cca-calib-cta cca-calib-cta--wide">
            <span className="cca-calib-cta-glow" aria-hidden="true" />
            <div className="cca-calib-cta-orb">
              <span className="cca-calib-cta-orb-ring" aria-hidden="true" />
              <span className="cca-calib-cta-orb-ring cca-calib-cta-orb-ring--2" aria-hidden="true" />
              <i className="fas fa-wand-magic-sparkles" />
            </div>
            <div className="cca-calib-cta-text">
              <div className="cca-calib-cta-eyebrow">
                <span className="cca-calib-cta-eyebrow-dot" />
                {cfg.title}
              </div>
              <div className="cca-calib-cta-title">{ctaCopy.heading}</div>
              <div className="cca-calib-cta-sub">{ctaCopy.sub}</div>
            </div>
            <div className="cca-calib-cta-metas">
              <span className="cca-calib-cta-meta">
                <i className="fas fa-bolt" /> ~30 seconds
              </span>
              <span className="cca-calib-cta-meta">
                <i className="fas fa-hand-pointer" /> Swipe to teach
              </span>
              <span className="cca-calib-cta-meta">
                <i className="fas fa-fingerprint" /> Sharpens your voice
              </span>
            </div>
            <button
              className="cca-calib-cta-btn"
              onClick={() => startBatch()}
              disabled={generating}
              type="button"
            >
              {generating ? (
                <><i className="fas fa-spinner fa-spin" /> {cfg.generatingText}</>
              ) : (
                <>
                  <i className={`fas ${ctaCopy.icon}`} />
                  {ctaCopy.label}
                </>
              )}
            </button>
            {generating && <GeneratingProgress mode={cfg.mode} progress={genProgress} />}
            {errorBlock}
          </div>
        )}

        {/* Active deck */}
        {deck.length > 0 && current && (
          <>
            <div className="cca-calib-progress">
              Card {progress + 1} of {deck.length}
              <div className="cca-calib-progress-bar">
                <span style={{ width: `${((progress + 1) / deck.length) * 100}%` }} />
              </div>
            </div>
            {(() => {
              const cat = cfg.categories.find((c) => c.id === current.category);
              return (
                <DeckCard
                  sample={current}
                  categoryLabel={cat?.label || current.category}
                  categoryIcon={cat?.icon || "fa-tag"}
                  exitDir={exitDir}
                  hint={cfg.cardHint}
                />
              );
            })()}
            <div className="cca-calib-actions">
              <button
                type="button"
                className="cca-calib-btn cca-calib-btn--reject"
                onClick={() => swipe(false)}
                disabled={!!exitDir}
                title="That's not how I'd write it (← / Backspace)"
              >
                <i className="fas fa-xmark" /> {cfg.rejectText}
              </button>
              <button
                type="button"
                className="cca-calib-btn cca-calib-btn--approve"
                onClick={() => swipe(true)}
                disabled={!!exitDir}
                title="Yes, that sounds like me (→ / Enter)"
              >
                <i className="fas fa-check" /> {cfg.approveText}
              </button>
            </div>
            <div className="cca-calib-keyhint">
              ← {cfg.rejectText} &nbsp;·&nbsp; → {cfg.approveText}
            </div>
          </>
        )}

        {/* Session complete */}
        {deck.length > 0 && index >= deck.length && (
          <div className="cca-calib-done">
            <div className="cca-calib-done-icon">
              <i className="fas fa-circle-check" />
            </div>
            <h4>Session saved</h4>
            <p>{cfg.doneBody}</p>
            <button
              className="cca-calib-cta-btn"
              onClick={() => {
                setDeck([]);
                setIndex(0);
              }}
              type="button"
            >
              <i className="fas fa-rotate" /> Run another batch
            </button>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default CalibrationDeck;
